import React, { useMemo, useState } from 'react';
import { Clock, Pencil } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { formatEntryDate, minutesBetween, itemTimeMinutes } from './workItemQueueFilters';

const emptyEdit = () => ({
  date: '',
  startTime: '',
  endTime: '',
  mins: '',
  note: '',
  billable: false,
});

/**
 * Time totals + editable entry list + logger (R-7710 corrections).
 */
const WorkItemTimeTab = ({
  detail,
  addTimeEntry,
  updateTimeEntry,
  showAlert,
  onItemUpdated,
  resolveName,
  profileSub,
}) => {
  const [showEntries, setShowEntries] = useState(true);
  const [timeDate, setTimeDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [timeStart, setTimeStart] = useState('');
  const [timeEnd, setTimeEnd] = useState('');
  const [timeMins, setTimeMins] = useState('30');
  const [timeNote, setTimeNote] = useState('');
  const [timeBillable, setTimeBillable] = useState(!!detail?.billable);
  const [editingId, setEditingId] = useState(null);
  const [edit, setEdit] = useState(emptyEdit);
  const [saving, setSaving] = useState(false);

  const derivedMins = useMemo(() => minutesBetween(timeStart, timeEnd), [timeStart, timeEnd]);
  const editDerivedMins = useMemo(
    () => minutesBetween(edit.startTime, edit.endTime),
    [edit.startTime, edit.endTime],
  );
  const entries = (detail.timeEntries || []).filter((e) => !e.removedAt);
  const total = detail.timeTotals?.totalMinutes ?? itemTimeMinutes(detail);
  const billable =
    detail.timeTotals?.billableMinutes ??
    entries.filter((e) => e.billable).reduce((s, e) => s + (e.durationMinutes || 0), 0);

  const person = (userId) => {
    if (!userId) return 'Unknown';
    if (profileSub && String(userId) === String(profileSub)) return 'You';
    return resolveName?.(userId) || 'Team member';
  };

  const beginEdit = (entry) => {
    setEditingId(entry.id);
    setEdit({
      date: entry.date || '',
      startTime: entry.startTime || '',
      endTime: entry.endTime || '',
      mins: String(entry.durationMinutes || ''),
      note: entry.note || '',
      billable: !!entry.billable,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEdit(emptyEdit());
  };

  const saveEdit = async () => {
    if (!editingId || !updateTimeEntry) return;
    setSaving(true);
    const patch = {
      date: edit.date,
      note: edit.note || null,
      billable: !!edit.billable,
    };
    if (edit.startTime && edit.endTime) {
      patch.startTime = edit.startTime;
      patch.endTime = edit.endTime;
    } else {
      patch.durationMinutes = Number(edit.mins);
    }
    const result = await updateTimeEntry(detail.id, editingId, patch);
    setSaving(false);
    if (result?.ok) {
      onItemUpdated(result.item);
      cancelEdit();
      showAlert(true, 'Time entry updated.', 'success', 3000);
    } else {
      showAlert(true, result?.message || 'Could not update time entry.', 'error', 5000);
    }
  };

  const removeEntry = async (entryId) => {
    if (!updateTimeEntry) return;
    if (!window.confirm('Remove this time entry? The correction stays in the activity log.')) return;
    setSaving(true);
    const result = await updateTimeEntry(detail.id, entryId, { remove: true });
    setSaving(false);
    if (result?.ok) {
      onItemUpdated(result.item);
      if (editingId === entryId) cancelEdit();
      showAlert(true, 'Time entry removed.', 'success', 3000);
    } else {
      showAlert(true, result?.message || 'Could not remove time entry.', 'error', 5000);
    }
  };

  return (
    <div className='space-y-4'>
      <div className='rounded-md border border-border bg-muted/30 p-3'>
        <p className='text-xs font-medium text-muted-foreground flex items-center gap-1 mb-1'>
          <Clock size={14} strokeWidth={1.5} />
          Totals
        </p>
        <p className='text-lg font-semibold tabular-nums text-foreground'>
          {total}m
          <span className='text-sm font-normal text-muted-foreground ml-2'>
            ({billable}m billable)
          </span>
        </p>
        <Button size='sm' variant='ghost' className='mt-1 px-0' onClick={() => setShowEntries((v) => !v)}>
          {showEntries ? 'Hide entries' : `Show entries (${entries.length})`}
        </Button>
      </div>

      {showEntries && (
        <ul className='space-y-2'>
          {entries.length === 0 ? (
            <li className='text-xs text-muted-foreground'>No entries yet</li>
          ) : (
            entries.map((e) => (
              <li key={e.id} className='rounded border border-border p-2 text-xs space-y-2'>
                {editingId === e.id ? (
                  <div className='space-y-2'>
                    <div className='grid grid-cols-2 gap-2'>
                      <div>
                        <label className='text-muted-foreground'>Date</label>
                        <Input
                          type='date'
                          value={edit.date}
                          onChange={(ev) => setEdit((s) => ({ ...s, date: ev.target.value }))}
                        />
                      </div>
                      <div>
                        <label className='text-muted-foreground'>
                          Minutes{editDerivedMins != null ? ' (from range)' : ''}
                        </label>
                        <Input
                          value={edit.mins}
                          disabled={editDerivedMins != null}
                          onChange={(ev) =>
                            setEdit((s) => ({
                              ...s,
                              mins: ev.target.value,
                              startTime: '',
                              endTime: '',
                            }))
                          }
                        />
                      </div>
                      <div>
                        <label className='text-muted-foreground'>Start</label>
                        <Input
                          type='time'
                          value={edit.startTime}
                          onChange={(ev) => {
                            const next = ev.target.value;
                            const mins = minutesBetween(next, edit.endTime);
                            setEdit((s) => ({
                              ...s,
                              startTime: next,
                              mins: mins != null ? String(mins) : s.mins,
                            }));
                          }}
                        />
                      </div>
                      <div>
                        <label className='text-muted-foreground'>End</label>
                        <Input
                          type='time'
                          value={edit.endTime}
                          onChange={(ev) => {
                            const next = ev.target.value;
                            const mins = minutesBetween(edit.startTime, next);
                            setEdit((s) => ({
                              ...s,
                              endTime: next,
                              mins: mins != null ? String(mins) : s.mins,
                            }));
                          }}
                        />
                      </div>
                    </div>
                    <div>
                      <label className='text-muted-foreground'>Note</label>
                      <Input
                        value={edit.note}
                        onChange={(ev) => setEdit((s) => ({ ...s, note: ev.target.value }))}
                      />
                    </div>
                    <label className='flex items-center gap-1.5 text-muted-foreground'>
                      <input
                        type='checkbox'
                        checked={edit.billable}
                        onChange={(ev) => setEdit((s) => ({ ...s, billable: ev.target.checked }))}
                      />
                      Billable
                    </label>
                    <div className='flex flex-wrap gap-2'>
                      <Button size='sm' loading={saving} onClick={saveEdit}>
                        Save correction
                      </Button>
                      <Button size='sm' variant='outline' onClick={cancelEdit} disabled={saving}>
                        Cancel
                      </Button>
                      <Button
                        size='sm'
                        variant='destructive'
                        disabled={saving}
                        onClick={() => removeEntry(e.id)}>
                        Remove
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className='flex items-start justify-between gap-2'>
                    <div className='min-w-0 space-y-0.5'>
                      <p className='font-medium text-foreground'>
                        {person(e.userId)}
                        <span className='font-normal text-muted-foreground'>
                          {' '}
                          · {formatEntryDate(e.date)}
                          {e.startTime && e.endTime ? ` ${e.startTime}–${e.endTime}` : ''}
                          {`: ${e.durationMinutes}m`}
                          {e.billable ? ' · billable' : ' · non-billable'}
                        </span>
                      </p>
                      {e.note ? <p className='text-muted-foreground'>{e.note}</p> : null}
                    </div>
                    {updateTimeEntry ? (
                      <Button
                        size='sm'
                        variant='ghost'
                        className='shrink-0'
                        onClick={() => beginEdit(e)}
                        aria-label='Edit time entry'>
                        <Pencil size={14} strokeWidth={1.5} />
                      </Button>
                    ) : null}
                  </div>
                )}
              </li>
            ))
          )}
        </ul>
      )}

      <div className='border-t border-border pt-3 space-y-2'>
        <p className='text-xs font-medium text-muted-foreground'>Log time</p>
        <div className='grid grid-cols-2 gap-2'>
          <div>
            <label className='text-xs text-muted-foreground'>Date</label>
            <Input type='date' value={timeDate} onChange={(e) => setTimeDate(e.target.value)} />
          </div>
          <div>
            <label className='text-xs text-muted-foreground'>
              Minutes{derivedMins != null ? ' (from range)' : ''}
            </label>
            <Input
              value={timeMins}
              onChange={(e) => {
                setTimeMins(e.target.value);
                setTimeStart('');
                setTimeEnd('');
              }}
              disabled={derivedMins != null}
            />
          </div>
          <div>
            <label className='text-xs text-muted-foreground'>Start</label>
            <Input
              type='time'
              value={timeStart}
              onChange={(e) => {
                const next = e.target.value;
                setTimeStart(next);
                const mins = minutesBetween(next, timeEnd);
                if (mins != null) setTimeMins(String(mins));
              }}
            />
          </div>
          <div>
            <label className='text-xs text-muted-foreground'>End</label>
            <Input
              type='time'
              value={timeEnd}
              onChange={(e) => {
                const next = e.target.value;
                setTimeEnd(next);
                const mins = minutesBetween(timeStart, next);
                if (mins != null) setTimeMins(String(mins));
              }}
            />
          </div>
        </div>
        <div className='flex gap-2 items-end'>
          <div className='flex-1'>
            <label className='text-xs text-muted-foreground'>Note</label>
            <Input value={timeNote} onChange={(e) => setTimeNote(e.target.value)} />
          </div>
          <Button
            size='sm'
            variant='outline'
            onClick={async () => {
              const body = {
                date: timeDate || new Date().toISOString().slice(0, 10),
                note: timeNote || null,
                billable: timeBillable,
              };
              if (timeStart && timeEnd) {
                body.startTime = timeStart;
                body.endTime = timeEnd;
              } else {
                body.durationMinutes = Number(timeMins);
              }
              const result = await addTimeEntry(detail.id, body);
              if (result?.ok) {
                onItemUpdated(result.item);
                setTimeNote('');
                setTimeStart('');
                setTimeEnd('');
              } else {
                showAlert(true, result?.message || 'Time entry failed.', 'error', 5000);
              }
            }}>
            Log time
          </Button>
        </div>
        <label className='flex items-center gap-1.5 text-xs text-muted-foreground'>
          <input
            type='checkbox'
            checked={timeBillable}
            onChange={(e) => setTimeBillable(e.target.checked)}
          />
          Billable
        </label>
      </div>
    </div>
  );
};

export default WorkItemTimeTab;
